import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Clock,
  Drum,
  GraduationCap,
  Layers,
  MapPin,
  Music,
} from "lucide-react";
import melams, { melamIntro } from "../data/melamsData";
import SEO from "../components/SEO";
import WhatsAppButton from "../components/WhatsAppButton";
import { PAGE_SEO, SITE_NAME } from "../data/seoData";
import { SEO_IMAGES } from "../data/seoImages";
import { BRAND_NAME } from "../data/contactInfo";
import { publicImage, MELAM_IMAGES } from "../data/pageImages";
import "../styles/Melams.css";

const ENROLL_SEARCH = { pathname: "/contact", search: "?type=course&form=open" };

const LEARNING_PATH = [
  {
    icon: BookOpen,
    title: "Fundamentals",
    text: "Veekku, Chaapu, basic talam, and Chenda handling — Edamthala and Valanthala techniques.",
  },
  {
    icon: Layers,
    title: "Chempada forms",
    text: "Chempada, Panchari, and progressive kaalam training through ensemble listening.",
  },
  {
    icon: Music,
    title: "Advanced Melams",
    text: "Adantha, Pandi, and Dhruvam — specialist forms for dedicated festival performers.",
  },
  {
    icon: Drum,
    title: "Live performance",
    text: "Temple programs and festival stages organised by the Sangam for real-world experience.",
  },
];

const Melams = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = melams[activeIndex];

  return (
    <main className="forms-page">
      <SEO
        title={PAGE_SEO.melams.title}
        description={PAGE_SEO.melams.description}
        path={PAGE_SEO.melams.path}
        ogImage={SEO_IMAGES.melams}
      />

      <header className="forms-hero">
        <img
          className="forms-hero__media"
          src={publicImage(MELAM_IMAGES.hero)}
          alt="Kerala Chenda Melam ensemble at a temple festival"
          width={1600}
          height={900}
          fetchPriority="high"
        />
        <div className="forms-hero__veil" aria-hidden="true" />
        <div className="forms-hero__content">
          <p className="forms-hero__brand">{BRAND_NAME}</p>
          <h1 className="forms-hero__title">Chenda Melam Forms in Chennai</h1>
          <p className="forms-hero__lead">
            Classical Kerala Melam traditions — Panchari, Pandi, Adantha, Chempada, and Dhruvam —
            taught and performed with temple-festival authenticity.
          </p>
          <div className="forms-hero__actions">
            <Link to="/chenda-classes-chennai" className="forms-btn forms-btn--primary">
              <GraduationCap size={18} aria-hidden="true" />
              Learn Melam
            </Link>
            <Link to="/chenda-melam-chennai" className="forms-btn forms-btn--ghost">
              Book Wedding Melam
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </header>

      <section className="forms-section forms-intro" aria-labelledby="forms-intro-heading">
        <div className="forms-section__inner forms-section__inner--narrow">
          <p className="forms-kicker">Tradition</p>
          <h2 id="forms-intro-heading" className="forms-heading">
            The classical Melam repertoire
          </h2>
          <p className="forms-lead">{melamIntro.subtitle}</p>
          <p className="forms-note">{melamIntro.note}</p>
        </div>
      </section>

      <section className="forms-section forms-explorer" aria-labelledby="forms-explorer-heading">
        <div className="forms-section__inner">
          <p className="forms-kicker">Explore forms</p>
          <h2 id="forms-explorer-heading" className="forms-heading">
            Select a Melam to study
          </h2>
          <p className="forms-lead">
            Each form has its own talam architecture, duration, and ritual context. Hover or tap to
            open the details.
          </p>

          <div className="forms-explorer__layout">
            <ul className="forms-rail" role="listbox" aria-label="Melam forms">
              {melams.map((melam, index) => (
                <li key={melam.id} role="option" aria-selected={index === activeIndex}>
                  <button
                    type="button"
                    className={`forms-rail__tab${index === activeIndex ? " is-active" : ""}`}
                    onClick={() => setActiveIndex(index)}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    <span className="forms-rail__index" aria-hidden="true">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="forms-rail__copy">
                      <span className="forms-rail__name">{melam.name}</span>
                      <span className="forms-rail__meta">{melam.category}</span>
                    </span>
                    <span className="forms-rail__bar" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>

            <article
              className="forms-panel"
              id={active.id}
              key={active.id}
              aria-live="polite"
            >
              <span className="forms-panel__category">{active.category}</span>
              <h3 className="forms-panel__name">{active.name}</h3>
              <p className="forms-panel__malayalam">{active.malayalam}</p>
              <p className="forms-panel__desc">{active.description}</p>

              <div className="forms-panel__meta">
                <div>
                  <Music size={16} aria-hidden="true" />
                  <div>
                    <span>Talam</span>
                    <strong>{active.talam}</strong>
                  </div>
                </div>
                <div>
                  <Clock size={16} aria-hidden="true" />
                  <div>
                    <span>Duration</span>
                    <strong>{active.duration}</strong>
                  </div>
                </div>
                <div>
                  <MapPin size={16} aria-hidden="true" />
                  <div>
                    <span>Context</span>
                    <strong>{active.context}</strong>
                  </div>
                </div>
              </div>

              <div className="forms-panel__features">
                <h4>Key features</h4>
                <ul>
                  {active.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>

              <div className="forms-panel__ensemble">
                <span>Ensemble</span>
                <div>
                  {active.instruments.map((inst) => (
                    <em key={inst}>{inst}</em>
                  ))}
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="forms-section forms-path" aria-labelledby="forms-path-heading">
        <div className="forms-section__inner">
          <p className="forms-kicker">Training</p>
          <h2 id="forms-path-heading" className="forms-heading">
            Learning path at {SITE_NAME}
          </h2>
          <p className="forms-lead">
            Progress from stick fundamentals to festival-stage Melam under gurukul-style guidance.
          </p>
          <ol className="forms-path__list">
            {LEARNING_PATH.map(({ icon: Icon, title, text }, index) => (
              <li className="forms-path__step" key={title}>
                <span className="forms-path__num" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="forms-icon" aria-hidden="true">
                  <Icon size={20} strokeWidth={1.75} />
                </span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="forms-hero__actions forms-hero__actions--start">
            <Link to={ENROLL_SEARCH} className="forms-btn forms-btn--primary">
              Enquire about training
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <WhatsAppButton>WhatsApp</WhatsAppButton>
          </div>
        </div>
      </section>

      <section className="forms-cta-band" aria-labelledby="forms-cta-heading">
        <div className="forms-cta-band__inner">
          <h2 id="forms-cta-heading">Book Melam or start learning</h2>
          <p>
            Wedding ensembles, temple programs, and structured Chenda Melam classes — all rooted in
            these classical forms.
          </p>
          <div className="forms-hero__actions">
            <Link to="/chenda-melam-chennai" className="forms-btn forms-btn--primary">
              Wedding Melam
            </Link>
            <Link to="/chenda-classes-chennai" className="forms-btn forms-btn--ghost">
              Classes in Chennai
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Melams;
